import { renderMessageHtml } from '../../chat/linkPolicy.ts'
import { findContact, contactHandle } from '../../storage/pairingStore.ts'
import { loadMessages, onMessagesChanged, type ChatMessage } from '../../storage/chatStore.ts'
import { clearUnread } from '../../storage/unreadStore.ts'
import { getTempChatByKey, onTempChatsChanged } from '../../storage/tempChatStore.ts'
import { formatTempRemaining, sendTempChatTextByKey } from '../../tempChat/tempChatService.ts'
import { tempChatMessageContactId, tempChatUnreadId } from '../../tempChat/tempChatUtils.ts'
import { getSessionToken } from '../../storage/sessionStore.ts'
import { syncInbox } from '../../socket2/ZionSocketClient.ts'
import { setCurrentTempChatKey } from '../../app/zionApp.ts'
import { t, tf } from '../../i18n/index.ts'
import { attachComposerInputIosKeyboardFix, attachChatVisualViewportInsets, keepComposerInputFocused } from '../composerKeyboard.ts'
import { clearComposer, getComposerText, setComposerText, wireComposerEditable } from '../composerField.ts'
import { applyAppLayout } from '../layout.ts'
import { isOnlyEmoji } from '../emojiOnlyText.ts'
import { el, escapeHtml } from '../dom.ts'
import { getParam, navigate } from '../router.ts'
import type { RenderStaleCheck } from '../renderGuard.ts'

let tempCountdownTimer: number | null = null
let tempMessageUnsub: (() => void) | null = null
let tempSessionUnsub: (() => void) | null = null
let tempKeyboardCleanup: (() => void) | null = null
let tempComposerFieldCleanup: (() => void) | null = null

export function clearTempChatScreenHooks(): void {
  if (tempCountdownTimer != null) {
    window.clearInterval(tempCountdownTimer)
    tempCountdownTimer = null
  }
  if (tempMessageUnsub) {
    tempMessageUnsub()
    tempMessageUnsub = null
  }
  if (tempKeyboardCleanup) {
    tempKeyboardCleanup()
    tempKeyboardCleanup = null
  }
  if (tempComposerFieldCleanup) {
    tempComposerFieldCleanup()
    tempComposerFieldCleanup = null
  }
  if (tempSessionUnsub) {
    tempSessionUnsub()
    tempSessionUnsub = null
  }
  setCurrentTempChatKey('')
}

function messageVisualKey(m: ChatMessage): string {
  return `${m.id}|${m.text}|${m.pending ? 1 : 0}|${m.outgoing ? 1 : 0}`
}

function messagesChanged(prev: ChatMessage[], next: ChatMessage[]): boolean {
  if (prev.length !== next.length) return true
  for (let i = 0; i < prev.length; i++) {
    if (messageVisualKey(prev[i]) !== messageVisualKey(next[i])) return true
  }
  return false
}

export async function renderTempChatScreen(
  root: HTMLElement,
  isStale: RenderStaleCheck = () => false,
): Promise<void> {
  clearTempChatScreenHooks()

  const chatKey = getParam('chatKey').trim()
  let temp = getTempChatByKey(chatKey)
  if (!temp || temp.expiresAt <= Date.now()) {
    navigate('home')
    return
  }

  if (temp.status === 'pending_incoming' || temp.status === 'pending_outgoing') {
    navigate('home')
    return
  }

  const contact = await findContact(temp.contactId)
  if (isStale()) return
  if (!contact) {
    navigate('home')
    return
  }

  setCurrentTempChatKey(chatKey)
  clearUnread(tempChatUnreadId(chatKey))

  const messageStoreId = tempChatMessageContactId(chatKey)
  let lastPaintedMsgs: ChatMessage[] = []
  let paintRaf: number | null = null
  let pendingForceScroll = false

  applyAppLayout('chat')
  root.classList.add('screen-chat', 'screen-temp-chat')
  root.classList.remove('screen-dashboard')
  root.innerHTML = ''
  root.append(
    el(`
      <div class="temp-chat-shell chat-screen">
        <div class="chat-header">
          <header class="temp-chat-topbar">
            <button type="button" class="icon-btn temp-back-btn" id="btn-back">←</button>
            <div class="temp-chat-topbar-text">
              <h2 class="temp-chat-title">${escapeHtml(contactHandle(contact))}</h2>
              <small class="temp-chat-sub">${escapeHtml(t('temp_screen_subtitle'))}</small>
              <p class="temp-chat-countdown" id="temp-countdown"></p>
            </div>
            <span class="temp-chat-badge-top icon-btn-clock" aria-hidden="true"></span>
          </header>
          <div class="temp-chat-status" id="temp-status" hidden></div>
        </div>
        <div class="chat-messages temp-chat-messages" id="messages">
          <p class="status muted chat-empty">${escapeHtml(t('temp_empty'))}</p>
        </div>
        <div class="chat-footer chat-input-bar" id="chat-input-bar">
          <form class="chat-composer temp-chat-composer" id="composer">
            <div
              id="msg-input"
              class="msg-input is-empty"
              contenteditable="true"
              role="textbox"
              aria-multiline="false"
              data-placeholder="${escapeHtml(t('temp_input_placeholder'))}"
              inputmode="text"
              enterkeyhint="send"
            ></div>
            <button type="submit" id="btn-send" class="send-btn temp-send-btn" aria-label="${escapeHtml(t('temp_send'))}">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </form>
        </div>
      </div>
    `),
  )

  const messagesEl = root.querySelector('#messages') as HTMLElement
  const composer = root.querySelector('#composer') as HTMLFormElement
  const msgInput = composer.querySelector('#msg-input') as HTMLElement
  const sendBtn = composer.querySelector('#btn-send') as HTMLButtonElement
  attachComposerInputIosKeyboardFix(msgInput)
  msgInput.addEventListener('focus', () => {
    void import('../../notifications/audioUnlock.ts').then((m) => m.unlockNotificationAudio())
  })
  const footerBar = root.querySelector('#chat-input-bar') as HTMLElement
  tempKeyboardCleanup = attachChatVisualViewportInsets({
    app: root,
    footer: footerBar,
    messages: messagesEl,
    input: msgInput,
  })
  const countdownEl = root.querySelector('#temp-countdown') as HTMLElement
  const statusEl = root.querySelector('#temp-status') as HTMLElement

  const contactName = contactHandle(contact)

  function updateComposerState(): void {
    const live = getTempChatByKey(chatKey)
    const waiting = live?.status === 'pending_outgoing'
    msgInput.setAttribute('contenteditable', waiting ? 'false' : 'true')
    sendBtn.disabled = waiting
    if (waiting) {
      statusEl.hidden = false
      statusEl.className = 'temp-chat-status highlight'
      statusEl.textContent = tf('temp_waiting_accept', { name: contactName })
      msgInput.setAttribute('data-placeholder', t('temp_waiting_placeholder'))
    } else {
      statusEl.hidden = true
      msgInput.setAttribute('data-placeholder', t('temp_input_placeholder'))
    }
  }

  const doSendTemp = (): void => {
    const text = getComposerText(msgInput).trim()
    if (!text) return
    clearComposer(msgInput)
    void (async () => {
      try {
        await sendTempChatTextByKey(chatKey, text)
        keepComposerInputFocused(msgInput)
        schedulePaint(true)
      } catch (err) {
        setComposerText(msgInput, text)
        alert(String(err))
      }
    })()
  }

  tempComposerFieldCleanup = wireComposerEditable(msgInput, {
    onSubmit: doSendTemp,
  })

  function refreshCountdown(): void {
    const live = getTempChatByKey(chatKey)
    if (!live || live.expiresAt <= Date.now()) {
      navigate('home')
      return
    }
    const remaining = live.expiresAt - Date.now()
    countdownEl.textContent = tf('temp_expires_in', { remaining: formatTempRemaining(remaining) })
    countdownEl.classList.toggle('temp-countdown-urgent', remaining < 60_000)
    updateComposerState()
  }

  refreshCountdown()
  tempCountdownTimer = window.setInterval(refreshCountdown, 1000)

  root.querySelector('#btn-back')!.addEventListener('click', () => {
    setCurrentTempChatKey('')
    navigate('home')
  })

  function existingBubbleMap(): Map<string, HTMLElement> {
    const map = new Map<string, HTMLElement>()
    for (const node of messagesEl.querySelectorAll('.temp-bubble[data-msg-id]')) {
      const id = (node as HTMLElement).dataset.msgId
      if (id) map.set(id, node as HTMLElement)
    }
    return map
  }

  function scrollToBottom(smooth = false): void {
    if (smooth) {
      messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'smooth' })
    } else {
      messagesEl.scrollTop = messagesEl.scrollHeight
    }
  }

  function paintNow(forceScroll = false): void {
    void loadMessages(messageStoreId).then((msgs) => {
      if (!msgs.length) {
        if (!messagesEl.querySelector('.chat-empty')) {
          messagesEl.innerHTML = `<p class="status muted chat-empty">${escapeHtml(t('temp_empty_hint'))}</p>`
        }
        lastPaintedMsgs = []
        return
      }

      messagesEl.querySelector('.chat-empty')?.remove()
      const domBubbles = existingBubbleMap()
      const msgIds = new Set(msgs.map((m) => m.id))

      for (const [id, node] of domBubbles) {
        if (!msgIds.has(id)) node.remove()
      }

      const frag = document.createDocumentFragment()
      for (const m of msgs) {
        const prev = lastPaintedMsgs.find((p) => p.id === m.id)
        const existing = domBubbles.get(m.id)
        if (existing) {
          if (prev && messageVisualKey(prev) === messageVisualKey(m)) continue
          existing.replaceWith(renderTempBubble(m))
          continue
        }
        frag.append(renderTempBubble(m))
      }
      if (frag.childNodes.length) messagesEl.append(frag)
      lastPaintedMsgs = msgs

      if (forceScroll) scrollToBottom()
    })
  }

  function schedulePaint(forceScroll = false): void {
    pendingForceScroll = pendingForceScroll || forceScroll
    if (paintRaf != null) return
    paintRaf = requestAnimationFrame(() => {
      paintRaf = null
      const scroll = pendingForceScroll
      pendingForceScroll = false
      void loadMessages(messageStoreId).then((msgs) => {
        if (!messagesChanged(lastPaintedMsgs, msgs) && !scroll) return
        paintNow(scroll)
      })
    })
  }

  composer.addEventListener('submit', (e) => {
    e.preventDefault()
    doSendTemp()
  })

  tempMessageUnsub = onMessagesChanged((changedId: string) => {
    if (changedId !== messageStoreId && changedId !== '*') return
    schedulePaint(true)
  })

  tempSessionUnsub = onTempChatsChanged(() => {
    refreshCountdown()
    schedulePaint(false)
  })

  paintNow(true)

  if (isStale()) return

  const session = (await getSessionToken())?.trim()
  if (isStale()) return
  if (session) void syncInbox(session).then(() => {
    if (!isStale()) schedulePaint(true)
  })
}

function renderTempBubble(m: ChatMessage): HTMLElement {
  const pending = m.pending ? ' …' : ''
  const isSystem = m.id.startsWith('temp-sys-')
  const bubble = el(`
    <div class="bubble ${m.outgoing ? 'out' : 'in'} temp-bubble${isSystem ? ' temp-system' : ''}" data-msg-id="${escapeHtml(m.id)}">
      <p>${renderMessageHtml(m.text, escapeHtml)}${pending}</p>
      <time>${new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
    </div>
  `)
  if (!isSystem && isOnlyEmoji(m.text)) {
    bubble.classList.add('emoji-only-message')
    bubble.style.cssText =
      'background:transparent!important;background-image:none!important;border:none!important;box-shadow:none!important;padding:0!important;min-width:0!important;'
    const p = bubble.querySelector('p')
    if (p) {
      p.replaceChildren()
      const glyph = document.createElement('span')
      glyph.className = 'emoji-only-glyph'
      glyph.textContent = m.text.replace(/[\u200B\u200C\u2060\uFEFF\u00AD]/g, '').trim()
      glyph.setAttribute(
        'style',
        'font-size:56px!important;line-height:1.1!important;display:inline-block!important;font-family:"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif!important;',
      )
      p.append(glyph)
      if (pending) p.append(document.createTextNode(pending))
      p.style.cssText =
        'font-size:56px!important;line-height:1.1!important;margin:0!important;font-family:"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif!important;'
    }
  }
  return bubble
}
